import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Paperclip, Archive, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { AttachmentsDialog, GroupPicker } from "./Attachments";

const HW_STATUS = ["Brouillon", "Publié interne", "Archivé"];
const ATT_TYPES = [
  { v: "teacher_instruction", l: "Consigne" }, { v: "teacher_support", l: "Support de cours" },
  { v: "teacher_correction", l: "Corrigé" }, { v: "other", l: "Autre" },
];

export default function Homework() {
  const [group, setGroup] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState(null);
  const [att, setAtt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!group) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pedagogy/homework?group_id=${group}`); setRows(data);
      const s = await api.get(`/pedagogy/groups/${group}/subjects`); setSubjects(s.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group]);

  const openNew = () => setForm({ title: "", description: "", subject_id: subjects[0]?.id || "", assigned_date: new Date().toISOString().slice(0, 10), due_date: "", status: "Brouillon" });
  const save = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    setBusy(true);
    try { await api.post("/pedagogy/homework", { ...form, group_id: group }); toast.success("Devoir créé."); setForm(null); await load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };
  const archive = async (id) => { try { await api.patch(`/pedagogy/homework/${id}/archive`); await load(); } catch (e) { toast.error("Erreur"); } };

  return (
    <div data-testid="pedagogy-homework-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2"><BookOpen className="h-6 w-6 text-primary" />Devoirs</h1>
        <div className="flex items-center gap-2">
          <GroupPicker value={group} onChange={setGroup} testid="hw-group" />
          <Button onClick={openNew} disabled={!group} className="gap-2 rounded-full" data-testid="hw-new"><Plus className="h-4 w-4" />Nouveau devoir</Button>
        </div>
      </div>
      <Card className="border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70">
            <TableHead>Titre</TableHead><TableHead>Matière</TableHead><TableHead>Donné le</TableHead>
            <TableHead>À rendre</TableHead><TableHead>Statut</TableHead><TableHead>PJ</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Aucun devoir.</TableCell></TableRow>
                : rows.map((h) => (
                  <TableRow key={h.id} data-testid="hw-row">
                    <TableCell className="font-medium">{h.title}</TableCell>
                    <TableCell>{h.subject_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{h.assigned_date}</TableCell>
                    <TableCell className="text-sm text-slate-500">{h.due_date || "—"}</TableCell>
                    <TableCell><Badge variant="secondary">{h.status}</Badge></TableCell>
                    <TableCell><Badge variant="secondary">{h.attachments_count}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setAtt(h)} data-testid={`hw-att-${h.id}`}><Paperclip className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => archive(h.id)}><Archive className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="hw-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouveau devoir</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hw-title" />
            <Textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
              <SelectTrigger data-testid="hw-subject"><SelectValue placeholder="Matière" /></SelectTrigger>
              <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2">
              <div className="flex-1"><label className="text-xs text-slate-500">Donné le</label><Input type="date" value={form.assigned_date} onChange={(e) => setForm({ ...form, assigned_date: e.target.value })} /></div>
              <div className="flex-1"><label className="text-xs text-slate-500">À rendre</label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="hw-due" /></div>
            </div>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{HW_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Annuler</Button><Button onClick={save} disabled={busy} data-testid="hw-save">{busy ? "…" : "Créer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentsDialog open={!!att} onClose={() => setAtt(null)} parentType="homework" parentId={att?.id} types={ATT_TYPES} />
    </div>
  );
}
